import { decorateApiRequest,observeApiFailure } from '../features/error-reporting/models/errorReportingRuntime';
import axios from "axios";
import { observeClientReloadError } from "../utils/clientReload";
import { API_DOMAIN, Whatsapp_DOMAIN } from "./constants";
import { assertSessionCurrent, registerSessionReset, sessionTicket } from '../utils/sessionLifecycle';

// Attach the auth token to every request aimed at our own backends.
// Individual call sites no longer need to pass the accessToken header
// themselves (existing ones keep working — this only fills the gap).
const OWN_ORIGINS = [API_DOMAIN, Whatsapp_DOMAIN].filter(Boolean);

axios.interceptors.request.use((config) => {
  const url = config.url || "";
  const isOwn = url.startsWith("/") || OWN_ORIGINS.some((origin) => url.startsWith(origin));
  if (isOwn) {
    if (config.__srmInitiatingSessionTicket != null) assertSessionCurrent(config.__srmInitiatingSessionTicket);
    if (config.__srmErrorReportSessionTicket != null) assertSessionCurrent(config.__srmErrorReportSessionTicket);
    config.headers = config.headers || {};
    // Tells the main backend's usage counter this is the new web (analytics
    // only, never a permission). Only that server allows the header in CORS;
    // the legacy web build doesn't send it.
    if (url.startsWith("/") || (API_DOMAIN && url.startsWith(API_DOMAIN))) config.headers["X-SRM-Client"] = "new_web";
    const token = localStorage.getItem("accessToken");
    const suppliedToken = config.headers?.accessToken;
    if (!url.includes('/auth/login') && suppliedToken && suppliedToken !== token) {
      throw Object.assign(new Error('Your session changed. Request cancelled.'), { code: 'SESSION_CHANGED' });
    }
    config.__srmSessionTicket = sessionTicket();
    if (token && !config.headers?.accessToken) {
      config.headers = config.headers || {};
      config.headers.accessToken = token;
    }
  }
  return decorateApiRequest(config);
});

// ── Auto-logout on rejected/expired token ────────────────────────────────────
// The backend replies HTTP 200 with { success: false, message: "not logged in" }
// when token verification fails. AuthContext registers the actual logout via
// setOnUnauthorized (this module has no React access).
let onUnauthorized = null;
export const setOnUnauthorized = (fn) => { onUnauthorized = fn; };
let onForbidden = null;
export const setOnForbidden = (fn) => { onForbidden = fn; };

let lastUnauthorizedAt = 0;
registerSessionReset(() => { lastUnauthorizedAt = 0; });
// Shared by the axios interceptor below and the Socket.IO connect_error
// handler (SocketContext) — both reject on the same "not logged in" signal.
export const triggerUnauthorized = () => {
  // Parallel calls all fail at once when a session dies — fire logout once
  const now = Date.now();
  if (now - lastUnauthorizedAt > 5000) {
    lastUnauthorizedAt = now;
    onUnauthorized?.();
  }
};

axios.interceptors.response.use(
  (response) => {
    if (response.config?.__srmSessionTicket != null) assertSessionCurrent(response.config.__srmSessionTicket);
    const url = response.config?.url || "";
    const isOwn = url.startsWith("/") || OWN_ORIGINS.some((origin) => url.startsWith(origin));
    if (
      isOwn &&
      !url.includes("/auth/login") &&
      response.data?.success === false &&
      String(response.data?.message || "").trim().toLowerCase() === "not logged in"
    ) {
      triggerUnauthorized();
    }
    if (response.status >= 500) observeApiFailure({ config: response.config, response });
    return response;
  },
  async (error) => {
    if (error.config?.__srmSessionTicket != null) assertSessionCurrent(error.config.__srmSessionTicket);
    // A real HTTP 401 (not our own backend's 200-status "not logged in"
    // convention above) rejects the promise instead of resolving it, so it
    // has to be caught here rather than in the fulfilled handler.
    observeApiFailure(error);
    const url = error.config?.url || "";
    const isOwn = url.startsWith("/") || OWN_ORIGINS.some(origin => url.startsWith(origin));
    if (isOwn && await observeClientReloadError(error)) return Promise.reject(error);
    // Only a dead session logs out. Other 401s (e.g. "WATI accessToken issue")
    // are provider faults and must not sign staff out.
    if (isOwn && error.response?.status === 401 && !url.includes("/auth/login")
      && String(error.response?.data?.message || "").trim().toLowerCase() === "not logged in") {
      triggerUnauthorized();
    }
    if (error.response?.status === 403 && !url.includes("/auth/login")) {
      // Permission denial is not an authentication failure. Let the caller
      // retain its UI and surface the backend message; AccessProvider may
      // refresh permission truth silently, but must never reload/remount the
      // application or retry this rejected request.
      onForbidden?.(error);
    }
    return Promise.reject(error);
  }
);
